import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Users,
  MessageSquare,
  Search,
  RefreshCw,
  Loader,
  User,
  Mail,
  Phone,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  Trash2,
  FileText,
  Clock,
  Edit,
  Lock,
  Unlock,
  UserCog,
  MessageCircle,
  VolumeX,
  Download,
  Users as UsersIcon
} from 'lucide-react';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { useTheme } from '../../../contexts/ThemeContext';

const UsersAndGroups = () => {
  const { getProtectedTenantInfo } = useTenantProtection();
  const { isDark } = useTheme();
  
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showEditOwnerModal, setShowEditOwnerModal] = useState(false);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [editingOwnerUserId, setEditingOwnerUserId] = useState('');
  const [editingOwnerEmail, setEditingOwnerEmail] = useState('');
  const [processing, setProcessing] = useState(false);
  const [memberProfiles, setMemberProfiles] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerOwnerUserId, setRegisterOwnerUserId] = useState('');
  const [registerOwnerEmail, setRegisterOwnerEmail] = useState('');

  const getCurrentTenant = () => {
    return getProtectedTenantInfo();
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/users/${tenant.code}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
      } else {
        throw new Error('Failed to load users');
      }
    } catch (error) {
      console.error('Failed to load Line users:', error);
      toast.error('Failed to load Line users');
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      setLoading(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/groups/${tenant.code}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGroups(data.data || []);
      } else {
        throw new Error('Failed to load groups');
      }
    } catch (error) {
      console.error('Failed to load Line groups:', error);
      toast.error('Failed to load Line groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else {
      loadGroups();
    }
  }, [activeTab]);

  const handleRefresh = () => {
    if (activeTab === 'users') {
      loadUsers();
    } else {
      loadGroups();
    }
  };

  const handleViewDetails = (item) => {
    if (activeTab === 'users') {
      setSelectedUser(item);
    } else {
      setSelectedGroup(item);
      setMemberProfiles([]); // Reset member profiles when opening details
    }
    setShowDetailModal(true);
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user?')) {
      return;
    }

    try {
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/users/${tenant.code}/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        toast.success('User removed successfully');
        loadUsers();
      } else {
        throw new Error('Failed to remove user');
      }
    } catch (error) {
      console.error('Failed to remove user:', error);
      toast.error('Failed to remove user');
    }
  };

  const handleRemoveGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to remove this group?')) {
      return;
    }

    try {
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/groups/${tenant.code}/${groupId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        toast.success('Group removed successfully');
        loadGroups();
      } else {
        throw new Error('Failed to remove group');
      }
    } catch (error) {
      console.error('Failed to remove group:', error);
      toast.error('Failed to remove group');
    }
  };

  const handleEditGroupName = (group) => {
    setSelectedGroup(group);
    setEditingGroupName(group.groupName);
    setShowEditNameModal(true);
  };

  const handleSaveGroupName = async () => {
    if (!editingGroupName.trim()) {
      toast.error('Group name cannot be empty');
      return;
    }

    try {
      setProcessing(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/groups/${tenant.code}/${selectedGroup.id}/name`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ groupName: editingGroupName.trim() })
      });
      
      if (response.ok) {
        toast.success('Group name updated successfully');
        setShowEditNameModal(false);
        loadGroups();
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update group name');
      }
    } catch (error) {
      console.error('Failed to update group name:', error);
      toast.error(error.message || 'Failed to update group name');
    } finally {
      setProcessing(false);
    }
  };

  const handleEditOwner = (group) => {
    setSelectedGroup(group);
    setEditingOwnerUserId(group.ownerUserId || '');
    setEditingOwnerEmail(group.ownerEmail || '');
    setShowEditOwnerModal(true);
  };

  const handleSaveOwner = async () => {
    if (!editingOwnerUserId && !editingOwnerEmail.trim()) {
      toast.error('Please provide either User ID or Email');
      return;
    }

    try {
      setProcessing(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/groups/${tenant.code}/${selectedGroup.id}/owner`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          ownerUserId: editingOwnerUserId ? parseInt(editingOwnerUserId) : null,
          ownerEmail: editingOwnerEmail.trim() || null
        })
      });
      
      if (response.ok) {
        toast.success('Group owner updated successfully');
        setShowEditOwnerModal(false);
        loadGroups();
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update group owner');
      }
    } catch (error) {
      console.error('Failed to update group owner:', error);
      toast.error(error.message || 'Failed to update group owner');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleLock = async (group) => {
    const action = group.isLocked ? 'unlock' : 'lock';
    if (!window.confirm(`Are you sure you want to ${action} this group? ${group.isLocked ? 'Messages will be processed again.' : 'Incoming messages will be ignored.'}`)) {
      return;
    }

    try {
      setProcessing(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/groups/${tenant.code}/${group.id}/lock`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isLocked: !group.isLocked })
      });
      
      if (response.ok) {
        toast.success(`Group ${action}ed successfully`);
        loadGroups();
      } else {
        const data = await response.json();
        throw new Error(data.message || `Failed to ${action} group`);
      }
    } catch (error) {
      console.error(`Failed to ${action} group:`, error);
      toast.error(error.message || `Failed to ${action} group`);
    } finally {
      setProcessing(false);
    }
  };

  const handleSyncGroup = async (group) => {
    try {
      setProcessing(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/groups/${tenant.code}/${group.id}/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Group synced successfully from LINE');
        loadGroups();
      } else {
        throw new Error(data.message || 'Failed to sync group');
      }
    } catch (error) {
      console.error('Failed to sync group:', error);
      toast.error(error.message || 'Failed to sync group from LINE');
    } finally {
      setProcessing(false);
    }
  };

  const handleLoadMemberProfiles = async (group) => {
    try {
      setLoadingMembers(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/groups/${tenant.code}/${group.id}/members`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMemberProfiles(data.data.members || []);
        toast.success(`Loaded ${data.data.memberCount} member profiles`);
      } else {
        throw new Error(data.message || 'Failed to load member profiles');
      }
    } catch (error) {
      console.error('Failed to load member profiles:', error);
      toast.error(error.message || 'Failed to load member profiles');
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleRegisterGroup = async () => {
    if (!selectedGroup) return;
    
    if (!registerOwnerUserId && !registerOwnerEmail) {
      toast.error('Please provide either Owner User ID or Owner Email');
      return;
    }

    try {
      setProcessing(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/groups/${tenant.code}/${selectedGroup.id}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ownerUserId: registerOwnerUserId || null,
          ownerEmail: registerOwnerEmail || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Group registered successfully');
        setShowRegisterModal(false);
        loadGroups();
      } else {
        throw new Error(data.message || 'Failed to register group');
      }
    } catch (error) {
      console.error('Failed to register group:', error);
      toast.error(error.message || 'Failed to register group');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleAutoResponse = async (group) => {
    // Check current state: 'listening' = auto-response on, 'registered' = silent
    const isCurrentlyListening = group.registrationState === 'listening';
    const action = isCurrentlyListening ? 'disable' : 'enable';
    const confirmMessage = isCurrentlyListening 
      ? 'Disable auto-response? The bot will stay silent and only log conversations to notes.'
      : 'Enable auto-response? The bot will automatically respond to messages in this group. AI tokens will be consumed.';
    
    console.log('[AutoResponse] Toggle clicked:', { 
      groupId: group.id, 
      groupName: group.groupName,
      currentState: group.registrationState, 
      isCurrentlyListening,
      willSetAutoResponse: !isCurrentlyListening 
    });
    
    if (!window.confirm(confirmMessage)) {
      console.log('[AutoResponse] User cancelled');
      return;
    }

    try {
      setProcessing(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const url = `${backendUrl}/api/line-management/groups/${tenant.code}/${group.id}/auto-response`;
      
      console.log('[AutoResponse] Sending request:', { url, autoResponse: !isCurrentlyListening });
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ autoResponse: !isCurrentlyListening })
      });
      
      const data = await response.json();
      console.log('[AutoResponse] Response:', { status: response.status, ok: response.ok, data });
      
      if (response.ok) {
        toast.success(`Auto-response ${action}d successfully`);
        loadGroups();
      } else {
        throw new Error(data.message || `Failed to ${action} auto-response`);
      }
    } catch (error) {
      console.error(`[AutoResponse] Failed to ${action} auto-response:`, error);
      toast.error(error.message || `Failed to ${action} auto-response`);
    } finally {
      setProcessing(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lineDisplayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lineUserId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredGroups = groups.filter(group => 
    group.groupName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.groupId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'users'
                ? 'bg-blue-600 text-white'
                : isDark
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <User className="h-4 w-4" />
            <span>Users ({users.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('groups')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'groups'
                ? 'bg-blue-600 text-white'
                : isDark
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            <span>Groups ({groups.length})</span>
          </button>
        </div>

        <button
          onClick={handleRefresh}
          disabled={loading}
          className={`flex items-center space-x-2 px-4 py-2 border rounded-md ${
            isDark 
              ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          } disabled:opacity-50`}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder={`Search ${activeTab}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            isDark 
              ? 'border-gray-600 bg-gray-700 text-white' 
              : 'border-gray-300 bg-white text-gray-900'
          }`}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : activeTab === 'users' ? (
        <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    User
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Line User ID
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Contact
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Joined
                  </th>
                  <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <User className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                      <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        No users found
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.userId} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            {user.linePictureUrl ? (
                              <img src={user.linePictureUrl} alt={user.username} className="h-10 w-10 rounded-full" />
                            ) : (
                              <User className="h-5 w-5 text-gray-500" />
                            )}
                          </div>
                          <div className="ml-4">
                            <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username}
                            </div>
                            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {user.lineDisplayName || user.lineStatusMessage || user.department || 'No info'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {user.lineUserId || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {user.email && (
                            <div className={`text-sm flex items-center space-x-1 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                              <Mail className="h-3 w-3" />
                              <span>{user.email}</span>
                            </div>
                          )}
                          {user.phone && (
                            <div className={`text-sm flex items-center space-x-1 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                              <Phone className="h-3 w-3" />
                              <span>{user.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {user.followStatus && (
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              Line: {user.followStatus}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm flex items-center space-x-1 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          <Calendar className="h-3 w-3" />
                          <span>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : user.lineCreatedAt ? new Date(user.lineCreatedAt).toLocaleDateString() : 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleViewDetails(user)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleRemoveUser(user.userId)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Group Name
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Owner
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Notes
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Latest Message
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredGroups.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <MessageSquare className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                      <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        No groups found
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredGroups.map((group) => (
                    <tr key={group.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-200'} relative`}>
                            <MessageSquare className="h-5 w-5 text-gray-500" />
                            {group.isLocked && (
                              <div className="absolute -top-1 -right-1 bg-orange-500 rounded-full p-0.5">
                                <Lock className="h-3 w-3 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className={`flex items-center space-x-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              <span className="text-sm font-medium">
                                {group.actualLineGroupName || group.groupName || 'Unknown Group'}
                              </span>
                              {group.isLocked && (
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded-full flex items-center space-x-1">
                                  <Lock className="h-3 w-3" />
                                  <span>Locked</span>
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <div className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {group.groupId || 'N/A'}
                              </div>
                              {group.memberCount !== null && group.memberCount !== undefined && (
                                <div className={`text-xs flex items-center space-x-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  <UsersIcon className="h-3 w-3" />
                                  <span>{group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <User className="h-4 w-4 text-gray-500" />
                          </div>
                          <div className="ml-3">
                            <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {group.ownerFirstName && group.ownerLastName 
                                ? `${group.ownerFirstName} ${group.ownerLastName}`
                                : group.ownerUsername || 'Not assigned'}
                            </div>
                            {group.ownerEmail && (
                              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {group.ownerEmail}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`flex items-center space-x-2 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          <FileText className="h-4 w-4 text-blue-500" />
                          <span className="text-sm font-semibold">
                            {group.noteCount || 0}
                          </span>
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {group.noteCount === 1 ? 'note' : 'notes'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {group.latestNoteDate ? (
                          <div className={`text-sm flex items-center space-x-1 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            <Clock className="h-3 w-3 text-green-500" />
                            <span>{new Date(group.latestNoteDate).toLocaleString()}</span>
                          </div>
                        ) : (
                          <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            No messages yet
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            group.registrationState === 'registered' || group.registrationState === 'listening'
                              ? 'bg-green-100 text-green-800' 
                              : group.registrationState === 'awaiting_confirmation' || group.registrationState === 'awaiting_email'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {group.registrationState || 'N/A'}
                          </span>
                          {(group.registrationState === 'awaiting_confirmation' || group.registrationState === 'awaiting_email') && (
                            <button
                              onClick={() => {
                                setSelectedGroup(group);
                                setRegisterOwnerUserId(group.ownerUserId || '');
                                setRegisterOwnerEmail(group.ownerEmail || '');
                                setShowRegisterModal(true);
                              }}
                              className={`text-xs px-2 py-1 rounded-md ${
                                isDark 
                                  ? 'bg-green-700 text-white hover:bg-green-600' 
                                  : 'bg-green-100 text-green-800 hover:bg-green-200'
                              }`}
                              title="Register Group"
                            >
                              Register
                            </button>
                          )}
                          {(group.registrationState === 'registered' || group.registrationState === 'listening') && (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full items-center space-x-1 ${
                              group.registrationState === 'listening'
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {group.registrationState === 'listening' ? (
                                <>
                                  <MessageCircle className="h-3 w-3" />
                                  <span>Auto-Reply</span>
                                </>
                              ) : (
                                <>
                                  <VolumeX className="h-3 w-3" />
                                  <span>Silent</span>
                                </>
                              )}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewDetails(group)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleSyncGroup(group)}
                            className="text-cyan-600 hover:text-cyan-900"
                            title="Sync from LINE"
                            disabled={processing}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditGroupName(group)}
                            className="text-green-600 hover:text-green-900"
                            title="Edit Name"
                            disabled={processing}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditOwner(group)}
                            className="text-purple-600 hover:text-purple-900"
                            title="Change Owner"
                            disabled={processing}
                          >
                            <UserCog className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleAutoResponse(group)}
                            className={`${group.registrationState === 'listening' ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-600'}`}
                            title={group.registrationState === 'listening' ? 'Auto-Response ON (click to disable)' : 'Auto-Response OFF (click to enable)'}
                            disabled={processing || group.isLocked || (group.registrationState !== 'registered' && group.registrationState !== 'listening')}
                          >
                            {group.registrationState === 'listening' ? <MessageCircle className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => handleToggleLock(group)}
                            className={`${group.isLocked ? 'text-orange-600 hover:text-orange-900' : 'text-gray-600 hover:text-gray-900'}`}
                            title={group.isLocked ? 'Unlock Group' : 'Lock Group'}
                            disabled={processing}
                          >
                            {group.isLocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => handleRemoveGroup(group.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Remove Group"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl max-w-2xl w-full mx-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {activeTab === 'users' ? 'User Details' : 'Group Details'}
              </h3>
              <div className="space-y-4">
                {activeTab === 'users' && selectedUser && (
                  <>
                    <div className="flex items-center space-x-4">
                      {selectedUser.linePictureUrl ? (
                        <img src={selectedUser.linePictureUrl} alt={selectedUser.username} className="h-20 w-20 rounded-full" />
                      ) : (
                        <div className={`h-20 w-20 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <User className="h-10 w-10 text-gray-500" />
                        </div>
                      )}
                      <div>
                        <h4 className={`text-xl font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {selectedUser.firstName && selectedUser.lastName ? `${selectedUser.firstName} ${selectedUser.lastName}` : selectedUser.username}
                        </h4>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>@{selectedUser.username}</p>
                        {selectedUser.jobTitle && (
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{selectedUser.jobTitle}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* System User Information */}
                    <div className={`border-t pt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <h5 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>System User Information</h5>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>User ID</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{selectedUser.userId}</p>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Username</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{selectedUser.username}</p>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Email</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{selectedUser.email || 'N/A'}</p>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Phone</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{selectedUser.phone || 'N/A'}</p>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Department</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{selectedUser.department || 'N/A'}</p>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</p>
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            selectedUser.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedUser.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Line Information */}
                    <div className={`border-t pt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <h5 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Line Information</h5>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Line ID</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{selectedUser.lineId || 'N/A'}</p>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Line User ID</p>
                          <p className={`text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{selectedUser.lineUserId || 'N/A'}</p>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Display Name</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{selectedUser.lineDisplayName || 'N/A'}</p>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Follow Status</p>
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            selectedUser.followStatus === 'following'
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {selectedUser.followStatus || 'N/A'}
                          </span>
                        </div>
                        {selectedUser.lineStatusMessage && (
                          <div className="col-span-2">
                            <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status Message</p>
                            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{selectedUser.lineStatusMessage}</p>
                          </div>
                        )}
                        {selectedUser.firstInteractionAt && (
                          <div>
                            <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>First Interaction</p>
                            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                              {new Date(selectedUser.firstInteractionAt).toLocaleString()}
                            </p>
                          </div>
                        )}
                        {selectedUser.lastInteractionAt && (
                          <div>
                            <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Last Interaction</p>
                            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                              {new Date(selectedUser.lastInteractionAt).toLocaleString()}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {activeTab === 'groups' && selectedGroup && (
                  <>
                    <div className="flex items-center space-x-4">
                      <div className={`h-20 w-20 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <MessageSquare className="h-10 w-10 text-gray-500" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h4 className={`text-xl font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {selectedGroup.actualLineGroupName || selectedGroup.groupName || 'Unknown Group'}
                          </h4>
                          {selectedGroup.isLocked && (
                            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full flex items-center space-x-1">
                              <Lock className="h-3 w-3" />
                              <span>Locked</span>
                            </span>
                          )}
                        </div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Registration: {selectedGroup.registrationState || 'N/A'}
                        </p>
                        {selectedGroup.actualLineGroupName && selectedGroup.actualLineGroupName !== selectedGroup.groupName && (
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                            Local name: {selectedGroup.groupName || 'N/A'}
                          </p>
                        )}
                        {selectedGroup.memberCount !== null && selectedGroup.memberCount !== undefined && (
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1 flex items-center space-x-1`}>
                            <UsersIcon className="h-3 w-3" />
                            <span>{selectedGroup.memberCount} {selectedGroup.memberCount === 1 ? 'member' : 'members'}</span>
                            {selectedGroup.lastSyncedAt && (
                              <span className="ml-2">(Synced: {new Date(selectedGroup.lastSyncedAt).toLocaleString()})</span>
                            )}
                          </p>
                        )}
                        {selectedGroup.isLocked && (
                          <p className={`text-xs ${isDark ? 'text-orange-400' : 'text-orange-600'} mt-1`}>
                            🔒 Incoming messages are being ignored
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>LINE Group ID</p>
                        <p className={`text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{selectedGroup.groupId || 'N/A'}</p>
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Database ID</p>
                        <p className={`text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{selectedGroup.id || 'N/A'}</p>
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Notes</p>
                        <div className={`flex items-center space-x-2 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          <FileText className="h-4 w-4 text-blue-500" />
                          <span className="text-sm font-semibold">{selectedGroup.noteCount || 0}</span>
                        </div>
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Member Count</p>
                        <div className={`flex items-center space-x-2 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          <UsersIcon className="h-4 w-4 text-purple-500" />
                          <span className="text-sm font-semibold">{selectedGroup.memberCount !== null && selectedGroup.memberCount !== undefined ? selectedGroup.memberCount : 'N/A'}</span>
                        </div>
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Created At</p>
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {selectedGroup.createdAt ? new Date(selectedGroup.createdAt).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Last Updated</p>
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {selectedGroup.updatedAt ? new Date(selectedGroup.updatedAt).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Latest Message</p>
                        {selectedGroup.latestNoteDate ? (
                          <div className={`flex items-center space-x-1 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            <Clock className="h-3 w-3 text-green-500" />
                            <span className="text-sm">{new Date(selectedGroup.latestNoteDate).toLocaleString()}</span>
                          </div>
                        ) : (
                          <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No messages yet</span>
                        )}
                      </div>
                      {selectedGroup.lastSyncedAt && (
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Last Synced</p>
                          <div className={`flex items-center space-x-1 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            <Download className="h-3 w-3 text-cyan-500" />
                            <span className="text-sm">{new Date(selectedGroup.lastSyncedAt).toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Owner</p>
                        <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {selectedGroup.ownerFirstName && selectedGroup.ownerLastName 
                            ? `${selectedGroup.ownerFirstName} ${selectedGroup.ownerLastName}`
                            : selectedGroup.ownerUsername || 'Not assigned'}
                        </div>
                        {selectedGroup.ownerEmail && (
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {selectedGroup.ownerEmail}
                          </div>
                        )}
                      </div>
                      {selectedGroup.verifiedAt && (
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Verified At</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {new Date(selectedGroup.verifiedAt).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {selectedGroup.members && selectedGroup.members.length > 0 && (
                        <div className="col-span-2">
                          <div className="flex items-center justify-between mb-2">
                            <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              Members ({selectedGroup.members.length})
                            </p>
                            <button
                              onClick={() => handleLoadMemberProfiles(selectedGroup)}
                              disabled={loadingMembers}
                              className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white disabled:opacity-50 flex items-center space-x-1`}
                            >
                              {loadingMembers ? (
                                <>
                                  <Loader className="h-3 w-3 animate-spin" />
                                  <span>Loading...</span>
                                </>
                              ) : (
                                <>
                                  <UsersIcon className="h-3 w-3" />
                                  <span>Load Profiles</span>
                                </>
                              )}
                            </button>
                          </div>
                          
                          {memberProfiles.length > 0 ? (
                            <div className={`max-h-60 overflow-y-auto ${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded p-3`}>
                              <div className="space-y-2">
                                {memberProfiles.map((member, index) => (
                                  <div key={index} className={`flex items-center space-x-3 p-2 rounded ${isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-white hover:bg-gray-100'} transition-colors`}>
                                    {member.pictureUrl ? (
                                      <img 
                                        src={member.pictureUrl} 
                                        alt={member.displayName || member.userId}
                                        className="h-10 w-10 rounded-full"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-500' : 'bg-gray-200'} ${member.pictureUrl ? 'hidden' : ''}`}>
                                      <User className="h-5 w-5 text-gray-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} truncate`}>
                                        {member.displayName || member.userId}
                                      </div>
                                      <div className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'} truncate`}>
                                        {member.userId}
                                      </div>
                                      {member.statusMessage && (
                                        <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'} truncate mt-1`}>
                                          {member.statusMessage}
                                        </div>
                                      )}
                                      {member.error && (
                                        <div className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'} mt-1`}>
                                          Error: {member.error}
                                        </div>
                                      )}
                                    </div>
                                    {member.language && (
                                      <div className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-gray-500' : 'bg-gray-200'} ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {member.language.toUpperCase()}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className={`max-h-40 overflow-y-auto ${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded p-2`}>
                              <div className="grid grid-cols-2 gap-2">
                                {selectedGroup.members.map((memberId, index) => (
                                  <div key={index} className={`text-xs font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {memberId}
                                  </div>
                                ))}
                              </div>
                              <p className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'} text-center`}>
                                Click "Load Profiles" to see member details
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      {(selectedGroup.registrationState === 'registered' || selectedGroup.registrationState === 'listening') && (
                        <div className="col-span-2">
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Auto-Response Mode</p>
                          <div className={`flex items-center space-x-2 mt-1`}>
                            {selectedGroup.registrationState === 'listening' ? (
                              <>
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center space-x-1">
                                  <MessageCircle className="h-3 w-3" />
                                  <span>Auto-Reply Enabled</span>
                                </span>
                                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Bot will automatically respond to messages
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full flex items-center space-x-1">
                                  <VolumeX className="h-3 w-3" />
                                  <span>Silent Mode</span>
                                </span>
                                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Bot only logs conversations to notes
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Name Modal */}
      {showEditNameModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl max-w-md w-full mx-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Edit Group Name
              </h3>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={editingGroupName}
                    onChange={(e) => setEditingGroupName(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    placeholder="Enter group name"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-2">
                <button
                  onClick={() => setShowEditNameModal(false)}
                  disabled={processing}
                  className={`px-4 py-2 border rounded-md ${
                    isDark 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  } disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveGroupName}
                  disabled={processing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {processing ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Register Group Modal */}
      {showRegisterModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl max-w-md w-full mx-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Register Group
              </h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Register this group under a user account. The group will be activated and ready to use.
              </p>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Owner User ID
                  </label>
                  <input
                    type="number"
                    value={registerOwnerUserId}
                    onChange={(e) => setRegisterOwnerUserId(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    placeholder="Enter user ID (optional)"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Owner Email
                  </label>
                  <input
                    type="email"
                    value={registerOwnerEmail}
                    onChange={(e) => setRegisterOwnerEmail(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    placeholder="Enter owner email (optional)"
                  />
                </div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Provide at least one field. User ID will be validated against active users.
                </p>
              </div>
              <div className="mt-6 flex justify-end space-x-2">
                <button
                  onClick={() => setShowRegisterModal(false)}
                  disabled={processing}
                  className={`px-4 py-2 border rounded-md ${
                    isDark 
                      ? 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  } disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRegisterGroup}
                  disabled={processing}
                  className={`px-4 py-2 rounded-md text-white ${
                    isDark 
                      ? 'bg-green-700 hover:bg-green-600' 
                      : 'bg-green-600 hover:bg-green-700'
                  } disabled:opacity-50`}
                >
                  {processing ? 'Registering...' : 'Register Group'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Owner Modal */}
      {showEditOwnerModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl max-w-md w-full mx-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Change Group Owner
              </h3>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Owner User ID
                  </label>
                  <input
                    type="number"
                    value={editingOwnerUserId}
                    onChange={(e) => setEditingOwnerUserId(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    placeholder="Enter user ID (optional)"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Owner Email
                  </label>
                  <input
                    type="email"
                    value={editingOwnerEmail}
                    onChange={(e) => setEditingOwnerEmail(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      isDark 
                        ? 'border-gray-600 bg-gray-700 text-white' 
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    placeholder="Enter owner email (optional)"
                  />
                </div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Provide at least one field. User ID will be validated against active users.
                </p>
              </div>
              <div className="mt-6 flex justify-end space-x-2">
                <button
                  onClick={() => setShowEditOwnerModal(false)}
                  disabled={processing}
                  className={`px-4 py-2 border rounded-md ${
                    isDark 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  } disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOwner}
                  disabled={processing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {processing ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersAndGroups;

